/**
 * window.__videoProbe(file, opts) — universal video metadata probe.
 *
 * Tier ladder:
 *   1. mp4box      — via WebCodecsPipeline.probeCodec / __Mp4BoxProbe.
 *                    mp4/mov/m4v fast path, ~100 ms, returns rich metadata
 *                    (HDR detection via colr box, codec string parsing).
 *   2. HTMLVideoElement.onloadedmetadata — Chrome-decodable containers
 *                    (mp4, mov, webm, ogg). ~250 ms typical, default 8 s
 *                    timeout.
 *   3. FFmpegProcessor.probeMeta — universal floor. Handles every container
 *                    FFmpeg-WASM can demux (AVI, MKV, WMV, FLV, TS, 3GP,
 *                    OGV, plus everything in tier-1/2). ~1.2 s cold,
 *                    sub-second warm. Returns regex-parsed log metadata.
 *
 * Returns UNION shape with NORMALIZED field names (mp4box `videoCodec` and
 * ffmpeg `vcodec` both become `codec`):
 *   {
 *     width, height, fps, duration,
 *     codec,        // normalized video codec short name (h264|hevc|mpeg4|…)
 *     codecFull,    // RFC-6381 string when known (mp4box only), else ''
 *     acodec,       // audio codec short name
 *     rotation,     // display rotation in degrees (0|90|180|270)
 *     isHdr,        // HDR detection (mp4box tier only — tier-3 stays false)
 *     hdrFormat,    // 'hdr10' | 'hlg' | 'dolbyvision' | 'main10' | null
 *     probe_path,   // 'mp4box' | 'html5' | 'ffmpeg' | 'none'
 *     probe_ms      // wall-clock ms across all tiers attempted
 *   }
 *
 * Race protection: each call records a probeId per File reference. If a
 * newer call is started on the same File between two await points, the
 * older call resolves to `null` instead of clobbering shared state. This
 * is cheap (no AbortSignal threading into FFmpeg-WASM needed) and prevents
 * the "user picked file B, sees file A's metadata" footgun.
 *
 * Never throws. Returns `{ probe_path:'none', probe_ms:N }` on total failure
 * so callers can dispatch a file_rejected{reason:'no_dims'} telemetry event
 * and surface a localized error.
 *
 * Etalon ported from /enhance-video's FFmpeg-fallback pattern (commits
 * d8e7e5607 + 4a5ab0d5f). Designed to migrate every video tool eventually
 * to one regex parser, one telemetry name, one probe ladder. First consumer
 * is /video-upscaler.
 */
(function () {
'use strict';

if (typeof window === 'undefined') return;
if (window.__videoProbe) return;       // idempotent — multi-tool load safe

var DEFAULT_HTML5_TIMEOUT_MS = 8000;
var _latestProbeForFile = new WeakMap();   // file → integer probeId

// ── Container sniff (first 32 bytes) ────────────────────────────────────
//
// Reads the file's magic-number signature to route the probe DIRECTLY to
// the tier that can actually parse it, skipping wasted attempts.
//
// Real-world impact (measured BEFORE this layer existed):
//   * 0-byte file → tier1 instant null + tier2 8 s timeout + tier3 1.2 s
//                   error → user waits ~9.5 s for the rejection.
//     With sniff: 0 ms (size===0 short-circuit before any tier).
//   * AVI / WMV / FLV → tier1 instant null + tier2 ~100-300 ms <video>
//                       onerror + tier3 ~1.2 s = ~1.5 s total.
//     With sniff: ~1.2 s (skip tier1+tier2, straight to FFmpeg).
//
// Returns one of:
//   'mp4'     — ISO BMFF family (mp4 / mov / m4v / 3gp / heif). Tier 1 only.
//   'webm'    — Matroska/WebM (EBML magic). Tier 2 first; tier 3 if browser
//               can't decode (pure MKV, HEVC-in-MKV, etc.).
//   'avi'     — RIFF...AVI . Tier 3 directly (no browser AVI demuxer).
//   'wmv'     — ASF (Advanced Systems Format). Tier 3 directly.
//   'flv'     — Flash Video. Tier 3 directly.
//   'ts'      — MPEG-TS. Tier 3 directly.
//   'ogg'     — Ogg container (theora/vorbis). Tier 2 — Chrome decodes.
//   'empty'   — size === 0. Skip every tier.
//   'unknown' — no signature matched. Fall back to legacy 1→2→3 ladder.
//
// Errors (security, browser quirks on .slice) silently resolve to
// 'unknown' so the fallback ladder runs.
async function _detectContainer(file) {
    if (!file) return 'unknown';
    if (file.size === 0) return 'empty';
    var n = Math.min(file.size, 32);
    var buf;
    try {
        buf = await file.slice(0, n).arrayBuffer();
    } catch (_) { return 'unknown'; }
    var b = new Uint8Array(buf);
    if (b.length < 4) return 'unknown';

    // MP4 / MOV / 3GP: at offset 4 = "ftyp" (0x66 0x74 0x79 0x70). Also
    // some MOV variants start with moov/mdat/wide/free/skip boxes.
    if (b.length >= 8 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'mp4';
    if (b.length >= 8) {
        var box4 = String.fromCharCode(b[4], b[5], b[6], b[7]);
        if (box4 === 'moov' || box4 === 'mdat' || box4 === 'wide' || box4 === 'free' || box4 === 'skip') return 'mp4';
    }

    // EBML (Matroska / WebM): 1A 45 DF A3.
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) return 'webm';

    // RIFF...AVI : "RIFF" + 4-byte size + "AVI ".
    if (b.length >= 12 &&
        b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
        b[8] === 0x41 && b[9] === 0x56 && b[10] === 0x49 && b[11] === 0x20) return 'avi';

    // ASF (WMV): 30 26 B2 75 8E 66 CF 11.
    if (b.length >= 8 &&
        b[0] === 0x30 && b[1] === 0x26 && b[2] === 0xB2 && b[3] === 0x75 &&
        b[4] === 0x8E && b[5] === 0x66 && b[6] === 0xCF && b[7] === 0x11) return 'wmv';

    // FLV: "FLV" + version byte.
    if (b[0] === 0x46 && b[1] === 0x4C && b[2] === 0x56) return 'flv';

    // Ogg: "OggS".
    if (b.length >= 4 && b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) return 'ogg';

    // MPEG-TS: sync byte 0x47 at offset 0. Two-of-three guard: also check
    // payload_unit_start_indicator at b[1] & 0x40 (first packet usually
    // has this set on PAT/PMT). We can't see byte 188 in our 32-byte slice,
    // so we lean on PUSI to filter random data starting with 0x47.
    if (b[0] === 0x47 && (b[1] & 0x40)) return 'ts';

    return 'unknown';
}

async function _probe(file, opts) {
    opts = opts || {};
    if (!file) return { probe_path: 'none', probe_ms: 0, container: 'unknown' };

    // Race guard: bump per-file counter; capture our own id before any
    // await. If a subsequent _probe(sameFile) starts before we finish,
    // our isLatest() will return false and we resolve to null — letting
    // the newer call own the resolution.
    var prevId = _latestProbeForFile.get(file) || 0;
    var ourId = prevId + 1;
    _latestProbeForFile.set(file, ourId);
    var t0 = Date.now();

    function isLatest() { return _latestProbeForFile.get(file) === ourId; }
    function finish(result) {
        if (!isLatest()) return null;
        result.probe_ms = Date.now() - t0;
        return result;
    }

    // Sniff first to ROUTE — saves wasted tier attempts for known
    // containers. ~1-2 ms cost (32-byte slice + arrayBuffer).
    var container = await _detectContainer(file);
    if (!isLatest()) return null;

    // Fast-fail empty files — skip every tier (was ~9.5 s on the legacy
    // ladder thanks to the 8 s HTMLVideoElement timeout).
    if (container === 'empty') {
        return finish({ probe_path: 'none', container: container });
    }

    // Result shape builders share `container` so telemetry can correlate
    // sniff vs probe outcome (e.g. sniff=mp4 but probe_path=ffmpeg → MP4
    // header with non-mp4-decodable interior → likely-corrupt).
    function _mp4boxResult(r) {
        var snd = _mp4boxSound(r.tracks);
        return finish({
            width: r.width, height: r.height,
            fps: r.fps || 30, duration: r.duration || 0,
            codec: _mp4boxVideoCodec(r),
            codecFull: r.videoCodecFull || '',
            // QuickTime PCM / G.711 land under type 'metadata' in mp4box, so
            // `audioCodec` is empty for a camera clip whose sound is alaw —
            // the unfiltered track list still names it (see _mp4boxSound).
            acodec: r.audioCodec || snd.codec,
            audioTracks: snd.tracks,
            audioChannels: snd.channels,
            rotation: r.rotation || 0,
            isHdr: !!r.isHdr,
            hdrFormat: r.hdrFormat || null,
            probe_path: 'mp4box',
            container: container
        });
    }
    function _html5Result(r) {
        return finish({
            width: r.width, height: r.height,
            fps: r.fps || 30, duration: r.duration || 0,
            codec: '', codecFull: '', acodec: '',
            rotation: 0, isHdr: false, hdrFormat: null,
            probe_path: 'html5',
            container: container
        });
    }
    function _ffmpegResult(r) {
        // probeMeta returns `vcodec`/`acodec` — normalize to `codec`/`acodec`
        // so the blade's state.probe.codec contract stays uniform across
        // every tier. HDR-from-FFmpeg-log is deferred; the tonemap-capability
        // probe inside ai-upscale-pipeline.js handles the zimg gate at
        // run time so HDR-MKV still gets correct downstream behaviour.
        return finish({
            width: r.width, height: r.height,
            fps: r.fps || 30, duration: r.duration || 0,
            codec: r.vcodec || '', codecFull: '',
            acodec: r.acodec || '',
            audioTracks: (r.audioStreams && r.audioStreams.length) || (r.acodec ? 1 : 0),
            // FFmpeg's own list of the tracks (__audioTracks.parse): the only
            // tier that knows which of them FFmpeg can carry at all — a track it
            // names `none` (iPhone Spatial Audio) is left out of the converters'
            // stream map (CodecMuxRules.streamMapArgs).
            audioStreams: (r.audioStreams && r.audioStreams.length) ? r.audioStreams : null,
            audioChannels: r.audioChannels || 0,
            rotation: r.rotation || 0,
            isHdr: false, hdrFormat: null,
            probe_path: 'ffmpeg',
            container: container
        });
    }
    function _noneResult() {
        return finish({ probe_path: 'none', container: container });
    }

    /* ⭐ needCodec (2026-07-29): a tier result with dimensions but NO codec
       name satisfies a player, and is useless to anything that has to DECIDE —
       /video-converter picks stream-copy vs re-encode purely from the codec, and
       tier-2 (<video>) cannot report one. WebM therefore stopped at tier-2 and
       every WebM was planned as a full re-encode: 4.3 s of transcode where a
       0.2 s copy was correct. With the flag, a codec-less result escalates to
       the FFmpeg tier, which does name codecs. Off by default — existing callers
       (thumbnails, dimension checks) must not pay for a WASM probe. */
    var wantCodec = !!opts.needCodec;
    /* ⚠️ Judged on the RAW tier shape, so every tier's own spelling of the
       codec counts: mp4box's rich shape says `videoCodec`, the bare one
       `codec`, FFmpeg's `vcodec`. Until 2026-09-30 only the last two were
       asked, so with needCodec EVERY mp4/mov — the answer already in hand
       after ~50 ms of mp4box — escalated to the FFmpeg tier: the 30 MB core
       loaded before a plan existed, first probe p50 5.2 s / p90 14.6 s on
       phones (prod, 60 d, both converters), and a Convert pressed meanwhile
       ran with no plan at all. Guard — tests/js/video-probe-needcodec.test.mjs. */
    function enough(res) { return !!(res && res.width && res.height && (!wantCodec || res.codec || res.vcodec || res.videoCodec)); }

    var r;
    // Known-MP4-family: mp4box is the only tier that has the metadata
    // shape this code needs (HDR detection, RFC-6381 codec string). Skip
    // tier-2/3 unless mp4box returns no dims (corrupt MP4 → FFmpeg
    // fallback can still salvage a probe).
    if (container === 'mp4') {
        r = await _tier1Mp4Box(file);
        if (!isLatest()) return null;
        if (enough(r)) return _mp4boxResult(r);
        // mp4box failed on what's supposed to be MP4 → corrupt. Last-
        // ditch FFmpeg attempt (sometimes parses past header damage).
        r = await _tier3Ffmpeg(file);
        if (!isLatest()) return null;
        if (r && r.width && r.height) return _ffmpegResult(r);
        return _noneResult();
    }

    // WebM / EBML / Ogg — Chrome's <video> handles these natively most of
    // the time. Try tier-2 first; fall to tier-3 for HEVC-in-MKV / 10-bit
    // / other matroska variants Chrome doesn't decode.
    if (container === 'webm' || container === 'ogg') {
        r = await _tier2Html5(file, opts.html5TimeoutMs || DEFAULT_HTML5_TIMEOUT_MS);
        if (!isLatest()) return null;
        if (enough(r)) return _html5Result(r);
        r = await _tier3Ffmpeg(file);
        if (!isLatest()) return null;
        if (r && r.width && r.height) return _ffmpegResult(r);
        return _noneResult();
    }

    // AVI / WMV / FLV / TS — no browser has a demuxer for these. Skip the
    // ~100-300 ms <video> onerror dance and go straight to FFmpeg. This
    // is the biggest perceived-latency win of the sniff layer.
    if (container === 'avi' || container === 'wmv' || container === 'flv' || container === 'ts') {
        r = await _tier3Ffmpeg(file);
        if (!isLatest()) return null;
        if (r && r.width && r.height) return _ffmpegResult(r);
        return _noneResult();
    }

    // 'unknown' — fall back to the legacy sequential ladder. This
    // handles weird containers we haven't catalogued (HEIC sequences,
    // raw streams, etc.) and any corrupt file whose header didn't
    // match a known magic but might still be salvageable.
    r = await _tier1Mp4Box(file);
    if (!isLatest()) return null;
    if (enough(r)) return _mp4boxResult(r);
    r = await _tier2Html5(file, opts.html5TimeoutMs || DEFAULT_HTML5_TIMEOUT_MS);
    if (!isLatest()) return null;
    if (enough(r)) return _html5Result(r);
    r = await _tier3Ffmpeg(file);
    if (!isLatest()) return null;
    if (r && r.width && r.height) return _ffmpegResult(r);
    return _noneResult();
}

/* Tier-1 video codec in the probe's short vocabulary. The rich mp4box shape
   (WebCodecsPipeline.probeCodec) already says `videoCodec: 'h264'`; the bare
   one (__Mp4BoxProbe) carries only the RFC-6381 `codec` — normalised through
   CodecMuxRules, the one table for it, when the page has it. */
function _mp4boxVideoCodec(r) {
    if (r.videoCodec) return r.videoCodec;
    var c = String(r.codec || '').toLowerCase();
    if (!c) return '';
    try {
        if (window.CodecMuxRules && window.CodecMuxRules.videoCodecFromMp4Box) {
            return window.CodecMuxRules.videoCodecFromMp4Box([{ type: 'video', codec: c }]) || c;
        }
    } catch (_) {}
    return c;
}

/* Sound as the unfiltered mp4box track list tells it: mp4box types as 'audio'
   only the sample entries it knows, and files G.711 (`alaw`/`ulaw`) and
   QuickTime PCM (`sowt`/`twos`/`lpcm`/`in24`…) as 'metadata'. Those are the
   camera clips a converter is handed all day — reading them as «no audio»
   planned a copy of a PCM track into MP4, or showed «No audio» on a clip with
   sound. Only names CodecMuxRules maps to a real audio codec count: an Apple
   `mebx` or timecode track is not sound. */
var _KNOWN_AUDIO = /^(pcm_|aac$|mp3$|ac3$|eac3$|alac$|opus$|vorbis$|flac$)/;
function _mp4boxSound(tracks) {
    var out = { codec: '', tracks: 0, channels: 0 };
    if (!tracks || !tracks.length) return out;
    var rules = window.CodecMuxRules;
    for (var i = 0; i < tracks.length; i++) {
        var t = tracks[i];
        if (!t || t.type === 'video') continue;
        var name = '';
        try { name = rules && rules.audioCodecFromMp4Box ? rules.audioCodecFromMp4Box([t]) : ''; } catch (_) { name = ''; }
        if (t.type !== 'audio' && !_KNOWN_AUDIO.test(name)) continue;
        out.tracks++;
        if (!out.codec && _KNOWN_AUDIO.test(name)) out.codec = name;
        if (!out.channels && t.audio && t.audio.channel_count) out.channels = t.audio.channel_count;
    }
    return out;
}

function _tier1Mp4Box(file) {
    // WebCodecsPipeline.probeCodec returns the rich shape including HDR;
    // __Mp4BoxProbe is the bare shape. Prefer the richer one.
    if (window.WebCodecsPipeline && typeof window.WebCodecsPipeline.probeCodec === 'function') {
        return Promise.resolve()
            .then(function () { return window.WebCodecsPipeline.probeCodec(file); })
            .catch(function () { return null; });
    }
    if (typeof window.__Mp4BoxProbe === 'function') {
        return Promise.resolve()
            .then(function () { return window.__Mp4BoxProbe(file); })
            .catch(function () { return null; });
    }
    return Promise.resolve(null);
}

function _tier2Html5(file, timeoutMs) {
    return new Promise(function (resolve) {
        var url, v;
        var done = false;
        try { url = URL.createObjectURL(file); } catch (_) { return resolve(null); }
        try { v = document.createElement('video'); } catch (_) { return resolve(null); }
        v.preload = 'metadata';
        v.muted = true;
        var settle = function (val) {
            if (done) return; done = true;
            try { URL.revokeObjectURL(url); } catch (_) {}
            try { v.src = ''; v.load(); } catch (_) {}
            resolve(val);
        };
        v.onloadedmetadata = function () {
            settle({
                width: v.videoWidth || 0,
                height: v.videoHeight || 0,
                duration: isFinite(v.duration) ? v.duration : 0,
                fps: 30
            });
        };
        v.onerror = function () { settle(null); };
        // Outer guard — onerror does not fire on every browser/container
        // combo (notably some MKV variants in Chrome stall silently).
        setTimeout(function () { settle(null); }, timeoutMs);
        v.src = url;
    });
}

/* ⚠️⚠️ THE FFMPEG TIER IS A SINGLETON AND MUST BE SERIALISED.
   FFmpegProcessor wraps ONE WASM instance with ONE log buffer. Two probes in
   flight at once interleave their runs and both parse whichever log landed
   last: /video-converter fed three different files (h264/aac MP4, VP9/Opus
   WebM, MPEG-4/MP3 AVI) and every card showed the AVI's codecs — a wrong plan
   for two of three files, from a UI that looked perfectly healthy.
   A per-file race guard cannot catch this: the files ARE different, it is the
   ENGINE that is shared. So probes queue here, one at a time. Tools that probe
   a batch (a converter, a merger, any queue) get correctness for free; a single
   probe pays nothing. */
var _ffChain = Promise.resolve();
function _tier3Ffmpeg(file) {
    if (!window.FFmpegProcessor || typeof window.FFmpegProcessor.probeMeta !== 'function') {
        return Promise.resolve(null);
    }
    var run = _ffChain.then(function () {
        return window.FFmpegProcessor.probeMeta(file);
    }).catch(function () { return null; });
    // Keep the chain alive even when this probe rejects.
    _ffChain = run.then(function () {}, function () {});
    return run;
}

// ── seekPresented(videoEl, t, timeoutMs) → Promise<via> ─────────────────
//
// Seek a <video> and resolve only once the target frame has actually been
// PRESENTED for composition — i.e. when drawImage() will capture real
// pixels. `seeked` alone does not guarantee that: engines fire it when the
// seek algorithm completes, which can precede the decoded frame reaching
// the compositor (hardware decode paths, busy main thread). drawImage()
// right inside a `seeked` handler then captures a transparent canvas and
// every derived metric is exactly 0. Field telemetry on /video-upscaler:
// 4.1% of frame analyses (19/461 over 30 d) returned all-zero metrics from
// this race — desktop AND mobile, chrome/firefox/yandex alike.
//
// Wait ladder after `seeked`:
//   1. requestVideoFrameCallback — the spec signal for "a frame was
//      presented" (Chrome 87+, Safari 15.4+, Firefox 132+). Fires for the
//      seeked-to frame of a paused video.
//   2. 300 ms timer racing rVFC — if the frame was presented BEFORE the
//      callback was registered, no new frame ever comes on a paused video
//      and rVFC would wait forever; the timer proceeds once composition
//      has had time to happen.
//   3. double-rAF where rVFC is unavailable — two frame ticks give the
//      compositor a chance to consume the decoded frame.
//
// Seeking to the CURRENT position is spec'd to abort without a `seeked`
// event (Firefox honors this) — detected up front and skipped straight to
// the presentation wait.
//
// Resolves with how the wait ended: 'rvfc' | 'rvfc_timer' | 'raf' |
// 'already_there' variants | 'timeout' | 'seek_error'. Callers forward it
// to telemetry so the field distribution of each ladder rung stays
// measurable. NEVER rejects. Does not touch the element's `onseeked`
// property (addEventListener only) — safe next to caller-owned handlers.
function seekPresented(v, t, timeoutMs) {
    return new Promise(function (resolve) {
        var done = false;
        var outer = setTimeout(function () { finish('timeout'); }, timeoutMs || 4000);
        function finish(via) {
            if (done) return;
            done = true;
            clearTimeout(outer);
            v.removeEventListener('seeked', onSeeked);
            resolve(via);
        }
        function waitPresented(suffix) {
            if (typeof v.requestVideoFrameCallback === 'function') {
                v.requestVideoFrameCallback(function () { finish('rvfc' + suffix); });
                setTimeout(function () { finish('rvfc_timer' + suffix); }, 300);
            } else {
                requestAnimationFrame(function () {
                    requestAnimationFrame(function () { finish('raf' + suffix); });
                });
            }
        }
        function onSeeked() {
            v.removeEventListener('seeked', onSeeked);
            waitPresented('');
        }
        if (Math.abs((v.currentTime || 0) - t) < 0.01) {
            waitPresented('_already_there');
            return;
        }
        v.addEventListener('seeked', onSeeked);
        try { v.currentTime = t; } catch (_) { finish('seek_error'); }
    });
}

window.__videoProbe = _probe;
// Canonical ladder now lives in timbrica-core.js (loaded on every page)
// as window.__seekPresented — delegate so there is ONE implementation to
// maintain; the local copy above stays as a fallback for any exotic load
// order where core hasn't executed yet.
window.__videoProbe.seekPresented = window.__seekPresented || seekPresented;

})();
